import {
  ApiBlueprint,
  PageBlueprint,
  SubPageBlueprint,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import {
  compatWrapper,
  convertLegacyRouteRef,
} from '@backstage/core-compat-api';
import { RiKey2Line } from '@remixicon/react';

import { SecretsConfigsClient, secretsConfigsApiRef } from './api';
import { rootRouteRef } from './routes';

const secretsConfigsApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: secretsConfigsApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ discoveryApi, fetchApi }) =>
          new SecretsConfigsClient({ discoveryApi, fetchApi }),
      }),
    ),
});

// The parent page has no loader of its own — the sub-pages below attach to
// its `pages` input and render as tabs, with `/configuration` redirecting to
// the first tab. The plugin-level title/icon make the app derive a single
// "Configuration" sidebar item for it.
const configurationPage = PageBlueprint.make({
  params: {
    path: '/configuration',
    routeRef: convertLegacyRouteRef(rootRouteRef),
  },
});

const secretsSubPage = SubPageBlueprint.make({
  name: 'secrets',
  params: {
    path: 'secrets',
    title: 'Secrets',
    loader: () =>
      import('./components/SecretsPage').then(m =>
        compatWrapper(<m.SecretsPage />),
      ),
  },
});

const configsSubPage = SubPageBlueprint.make({
  name: 'configs',
  params: {
    path: 'configs',
    title: 'Configs',
    loader: () =>
      import('./components/ConfigsPage').then(m =>
        compatWrapper(<m.ConfigsPage />),
      ),
  },
});

const compareSubPage = SubPageBlueprint.make({
  name: 'compare',
  params: {
    path: 'compare',
    title: 'Compare',
    loader: () =>
      import('./components/ComparePage').then(m =>
        compatWrapper(<m.ComparePage />),
      ),
  },
});

const historySubPage = SubPageBlueprint.make({
  name: 'history',
  params: {
    path: 'history',
    title: 'History',
    loader: () =>
      import('./components/HistoryPage').then(m =>
        compatWrapper(<m.HistoryPage />),
      ),
  },
});

/**
 * The Backstage new-frontend-system version of the secrets-configs plugin.
 *
 * @public
 */
export default createFrontendPlugin({
  pluginId: 'secrets-configs',
  title: 'Configuration',
  icon: <RiKey2Line />,
  routes: {
    root: convertLegacyRouteRef(rootRouteRef),
  },
  extensions: [
    secretsConfigsApi,
    configurationPage,
    secretsSubPage,
    configsSubPage,
    compareSubPage,
    historySubPage,
  ],
});
